import { useStytch, useStytchUser } from '@stytch/nextjs';
import axios from 'axios';
import { Spinner } from 'components/app-components/ui/spinner';
import { useRouter } from 'next/router';
import { useMemo, useState } from 'react';
import { parsePhoneNumber } from 'react-phone-number-input/mobile';

import Button from 'components/app-components/ui/button';
import theraToast from 'components/generic-components/toast/TheraToast';
import { useUser } from 'context/user';
import { useModal } from 'hooks/useModal';
import CheckMark from 'icons/CheckMark';
import MFAIcon from 'icons/MFAIcon';
import RightArrow from 'icons/RightArrow';
import SMS from 'icons/SMS';
import TOTPIcon from 'icons/TOTPIcon';
import Whatsapp from 'icons/Whatsapp';
import { OTP_EXPIRATION_MINUTES, TOTP_ENROLLMENT_EXPIRATION_MINUTES } from 'lib/constants';
import { isCountrySupportedForOtpMethod } from 'lib/countryList';
import { AuthRoutes } from 'routes';
import { MFAMethodType, TOTPSetupData } from 'types/mfa';
import { formatErrorMessage } from 'utils/error';
import GenericModal from '../modals/GenericModal';
import PromptLogoutModal from '../modals/PromptLogoutModal';
import { getPhoneOTPErrorMessage, mfaMethodLabels } from './mfaMessages';
import PhoneOTPModal from './PhoneOTPModal';
import ValidateOTPModal from './ValidateOTPModal';

const initialMethods: MFAMethodType[] = [MFAMethodType.WHATSAPP, MFAMethodType.SMS, MFAMethodType.TOTP];
const mfaMethodIcons = {
  [MFAMethodType.TOTP]: <TOTPIcon className="size-6" />,
  [MFAMethodType.SMS]: <SMS className="size-6" />,
  [MFAMethodType.WHATSAPP]: <Whatsapp className="size-6" />,
};

export default function SetUpMFAModal({
  open,
  onClose,
  email,
  isProcessing,
  shouldShowLogoutConfirmation = false,
}: {
  open: boolean;
  onClose: () => void;
  email: string;
  isProcessing?: boolean;
  shouldShowLogoutConfirmation?: boolean;
}) {
  const { user, setTOTPSetupStatus, currentCompany, logout, currentContract } = useUser();
  const router = useRouter();
  const { mfaUserDetails } = user || {};
  const mfaType = mfaUserDetails?.mfaType;
  const stytchClient = useStytch();
  const { user: stytchUser } = useStytchUser();
  const totpId = stytchUser?.totps?.[0]?.totp_id;
  const isTotpVerified = stytchUser?.totps?.[0]?.verified;
  const otpModal = useModal();
  const phoneOTPModal = useModal();
  const logoutConfirmModal = useModal();
  const [selectedMethod, setSelectedMethod] = useState<MFAMethodType>(mfaType);
  const [emailIDForOTP, setEmailIDForOTP] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<MFAMethodType | null>(null);
  const [totpSetupData, setTotpSetupData] = useState<TOTPSetupData | null>(null);
  const userPhoneNumber = stytchUser?.phone_numbers?.[0]?.phone_number;
  const userPhoneNumberVerified = stytchUser?.phone_numbers?.[0]?.verified;
  const userPhoneNumberID = stytchUser?.phone_numbers?.[0]?.phone_id;
  const [phoneMethodID, setPhoneMethodID] = useState<string>('');

  // here if admin is onboarding then user wont have employeeInformation in this case we wanna use company country
  const profileCountry =
    user?.employeeInformation?.address?.country || currentContract?.country || currentCompany?.address?.country || 'US';
  // A verified number is where the code goes, so its country decides what can be delivered. Without
  // one, PhoneOTPModal checks the number the user types next.
  const deliveryCountry =
    (userPhoneNumberVerified && userPhoneNumber && parsePhoneNumber(userPhoneNumber)?.country) || profileCountry;
  const availableMethods = useMemo(
    () => initialMethods.filter((method) => isCountrySupportedForOtpMethod(method, deliveryCountry)),
    [deliveryCountry],
  );

  const handleSetup2FA = async (method: MFAMethodType) => {
    setSelectedMethod(method);
    setIsSubmitting(method);
    setTOTPSetupStatus(true);

    try {
      // Handle phone number verification for SMS and WhatsApp
      if ([MFAMethodType.SMS, MFAMethodType.WHATSAPP].includes(method)) {
        if (!userPhoneNumber || !userPhoneNumberVerified) {
          phoneOTPModal.handleOpen();
          return;
        }
      }

      // Returning to the authenticator step must reuse the registration the user already
      // scanned. Creating a second one silently voids the first QR code, so every code their
      // app produces from then on is rejected and retrying can never recover.
      if (method === MFAMethodType.TOTP && totpSetupData && totpSetupData.expiresAt > Date.now()) {
        otpModal.handleOpen();
        return;
      }

      // Reset previous state
      setTotpSetupData(null);
      setEmailIDForOTP('');
      setPhoneMethodID('');

      let otpRes;
      switch (method) {
        case MFAMethodType.TOTP: {
          if (totpId && !isTotpVerified) {
            await axios.delete('/api/mfa/delete-totp', {
              data: {
                totpId: totpId,
                // ...(userPhoneNumberVerified && { verifiedPhoneNumber: userPhoneNumber }),
              },
            });
          }
          const totpRes = await stytchClient.totps.create({
            expiration_minutes: TOTP_ENROLLMENT_EXPIRATION_MINUTES,
          });
          setTotpSetupData({
            qr_code: totpRes.qr_code,
            recovery_codes: totpRes.recovery_codes,
            secret: totpRes.secret,
            expiresAt: Date.now() + TOTP_ENROLLMENT_EXPIRATION_MINUTES * 60 * 1000,
          });
          break;
        }
        case MFAMethodType.SMS: {
          otpRes = await stytchClient.otps.sms.send(userPhoneNumber, {
            expiration_minutes: OTP_EXPIRATION_MINUTES,
          });
          setPhoneMethodID(otpRes.method_id);
          break;
        }
        case MFAMethodType.WHATSAPP: {
          otpRes = await stytchClient.otps.whatsapp.send(userPhoneNumber, {
            expiration_minutes: OTP_EXPIRATION_MINUTES,
          });
          setPhoneMethodID(otpRes.method_id);
          break;
        }
        case MFAMethodType.EMAIL: {
          otpRes = await stytchClient.otps.email.send(email, {
            expiration_minutes: OTP_EXPIRATION_MINUTES,
          });
          setEmailIDForOTP(otpRes.method_id);
          break;
        }
      }

      // Show success message for all methods except TOTP
      if (method !== MFAMethodType.TOTP) {
        const messageTarget =
          method === MFAMethodType.EMAIL ? 'email' : method === MFAMethodType.SMS ? 'phone number' : 'WhatsApp';
        theraToast.success(`OTP has been sent to your ${messageTarget}.`);
      }

      // Open OTP modal for all methods
      otpModal.handleOpen();
      otpRes = null;
    } catch (error) {
      if (error.error_type === 'user_unauthenticated') {
        await router.replace(AuthRoutes.SignIn);
        return;
      }
      if (error.error_type === 'stale_factors') {
        // for now, show a toast asking user to log out and log in again
        theraToast.warning('Your session has expired. Please log in again to continue.', {
          id: 'setup-mfa-stale_factors',
        });
        return;
      }
      const errorMsg = getPhoneOTPErrorMessage(error.error_type, method, 'Error occurred while setting up 2FA');
      const httpStatusCode = error?.status_code;
      const finalErrorMessage =
        httpStatusCode === 429
          ? 'Too many requests. Please try again later in some time or Contact Support.'
          : formatErrorMessage(error?.body, errorMsg);
      theraToast.error({
        message: finalErrorMessage,
        error: error,
        options: {
          id: 'setup-mfa-error',
        },
      });
    } finally {
      setIsSubmitting(null);
    }
  };

  const handleCloseModal = () => {
    if (shouldShowLogoutConfirmation) {
      logoutConfirmModal.handleOpen();
    } else {
      onClose();
    }
  };

  const handleConfirmLogout = () => {
    logoutConfirmModal.handleClose();
    logout();
  };

  const MFAMethodButton = ({
    method,
    isSubmitting,
    userSetMFAType,
    icon,
    label,
  }: {
    method: MFAMethodType;
    isSubmitting: MFAMethodType | null;
    userSetMFAType: MFAMethodType | null;
    icon: React.ReactNode;
    label: string;
  }) => {
    const _changePhoneNumber = async (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      // TODO: #003 - before deleting the phone number (as user has MFA attached to it), first add a new phone number and then delete the current one
      // first delete the phone number
      await stytchClient.user.deletePhoneNumber(userPhoneNumberID);
      // then open the phoneOTPModal
      phoneOTPModal.handleOpen();
    };

    return (
      <div className="[&>button]:rounded-none [&>button]:border-0 [&>button]:shadow-none">
        <Button
          variant="outline"
          onClick={() => handleSetup2FA(method)}
          className="flex !h-auto w-full items-center justify-between !p-4 hover:bg-neutral-50"
          disabled={(isSubmitting && isSubmitting !== method) || userSetMFAType === method}
          aria-label={`Set up ${label} authentication`}
        >
          <div className="flex items-center gap-4">
            <div className="w-13 h-13 rounded-lg border border-neutral-200 bg-neutral-50 p-4">{icon}</div>
            <p className="flex flex-col items-start gap-1 text-base font-medium text-neutral-900">
              <span>Set up using {label}</span>
              {(method === MFAMethodType.SMS || method === MFAMethodType.WHATSAPP) &&
                userPhoneNumber &&
                userPhoneNumberVerified && (
                  <span className="flex items-center gap-1 text-xs text-neutral-500">
                    <span>({userPhoneNumber})</span>
                    {/* <Button variant="plain" size="small" className="p-0" onClick={changePhoneNumber}>
                      <Edit className="w-3 h-3" />
                    </Button> */}
                  </span>
                )}
              {/* {method === MFAMethodType.TOTP && userPhoneNumberVerified && (
                <span className="flex items-center gap-1 text-xs text-neutral-500">
                  <span>This will delete the existing phone number.</span>
                </span>
              )} */}
            </p>
          </div>
          {isSubmitting === method ? <Spinner /> : userSetMFAType === method ? <CheckMark /> : <RightArrow />}
        </Button>
      </div>
    );
  };

  return (
    <>
      <GenericModal
        open={open}
        // this setup MFA modal is called at 2 places:
        // 1. when user clicks on "Enable/Edit 2FA" button on the account security page, here it will just close the modal on click of close button
        // 2. on 2FA HOC to force setup the 2FA on login/signup, here it will logout the user when closed.
        onClose={handleCloseModal}
        titleIcon={
          <div className="flex h-11 w-11 min-w-11 items-center justify-center rounded bg-neutral-100">
            <MFAIcon />
          </div>
        }
        title="Enable 2FA on your account"
        subTitle="Secure your account by adding a layer of protection with Two Factor Authentication. Please reach out to Thera support if you need help resetting your 2FA."
        width="w-[30rem]"
        showHeaderBorder={false}
        isProcessing={isSubmitting === 'EMAIL' || isSubmitting === 'TOTP' || isProcessing}
        preventOutsideClick={shouldShowLogoutConfirmation}
      >
        <div className="flex flex-col divide-y divide-neutral-200 overflow-hidden rounded-lg border border-neutral-200">
          {availableMethods.map((method) => (
            <MFAMethodButton
              key={method}
              method={method}
              isSubmitting={isSubmitting}
              userSetMFAType={mfaType}
              icon={mfaMethodIcons[method]}
              label={mfaMethodLabels[method]}
            />
          ))}
        </div>
      </GenericModal>
      {/* OTP modal is used to verify the OTP for all 2FA methods. */}
      {otpModal.isOpen && (
        <ValidateOTPModal
          open={otpModal.isOpen}
          // this modal is called at 2 places:
          // 1. When verifying the OTP if user already has 2FA setup. - 2FA HOC
          // 2. When user is setting up 2FA. - either in 2FA HOC or via Account Security Page but both via SetupMFAModal
          onClose={() => {
            // clear the TOTP setup status
            setTOTPSetupStatus(false);
            // trigger this modal itself to close
            otpModal.handleClose();
          }}
          methodID={emailIDForOTP || phoneMethodID}
          email={email}
          selectedMethod={selectedMethod || mfaType}
          totpData={totpSetupData}
          // Stytch no longer recognises the registration, so the cached QR code is dead and the
          // next attempt has to start from a fresh one.
          onTOTPRegistrationGone={() => setTotpSetupData(null)}
          saveUserMFA
          userPhoneNumber={userPhoneNumber}
        />
      )}
      {phoneOTPModal.isOpen && (
        <PhoneOTPModal
          open={phoneOTPModal.isOpen}
          onClose={phoneOTPModal.handleClose}
          setPhoneMethodID={setPhoneMethodID}
          userPhoneNumber={userPhoneNumber}
          userPhoneNumberVerified={userPhoneNumberVerified}
          openOTPModal={otpModal.handleOpen}
          method={selectedMethod}
        />
      )}
      {logoutConfirmModal.isOpen && shouldShowLogoutConfirmation && (
        <PromptLogoutModal
          open={logoutConfirmModal.isOpen}
          onClose={logoutConfirmModal.handleClose}
          onConfirm={handleConfirmLogout}
          title="2FA setup is required to proceed. This will log you out."
          isProcessing={isProcessing}
        />
      )}
    </>
  );
}
